import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Callout,
  EmptyState,
  Icon,
  SectionHeader,
  Spinner,
  Stat,
  Tabs,
  Tag,
} from "../ui";
import { useBuildReferences } from "../hooks/useReferences";
import {
  useSelectAll,
  useSources,
  useToggleSource,
  useValidateAccess,
} from "../hooks/useSources";
import { useJobPolling } from "../hooks/useJobPolling";
import type { Project, Source } from "../types";

type Filter = "all" | "selected" | "available";

export interface Step2SourcesProps {
  project: Project;
  onAdvance: (n: number) => void;
}

function fileBadge(src: Source): { tone: "rule" | "strike"; label: string } {
  if (src.accessibility_status === "unavailable") {
    return { tone: "strike", label: "Indisponível" };
  }
  if (src.file_type) {
    return { tone: "rule", label: src.file_type.toUpperCase() };
  }
  return { tone: "rule", label: src.accessibility_status === "unknown" ? "A validar" : "HTML" };
}

export function Step2Sources({ project, onAdvance }: Step2SourcesProps) {
  const qc = useQueryClient();
  const sources = useSources(project.id);
  const toggle = useToggleSource(project.id);
  const selectAll = useSelectAll(project.id);
  const validate = useValidateAccess(project.id);
  const buildReferences = useBuildReferences(project.id);
  const [filter, setFilter] = useState<Filter>("all");
  const [validateJobId, setValidateJobId] = useState<string | null>(null);
  const validateJob = useJobPolling(validateJobId);

  useEffect(() => {
    if (validateJob.isTerminal) {
      qc.invalidateQueries({ queryKey: ["sources", project.id] });
    }
  }, [project.id, qc, validateJob.isTerminal]);

  const allSources = sources.data ?? [];
  const selected = allSources.filter((s) => s.selected);
  const available = allSources.filter((s) => s.accessibility_status !== "unavailable");
  const filtered = useMemo(
    () =>
      allSources.filter(
        (s) =>
          filter === "all" ||
          (filter === "selected" && s.selected) ||
          (filter === "available" && s.accessibility_status !== "unavailable"),
      ),
    [allSources, filter],
  );

  const validateRunning =
    validate.isPending || validateJob.data?.status === "queued" || validateJob.data?.status === "running";

  const startValidation = async () => {
    const result = await validate.mutateAsync();
    setValidateJobId(result.job_id);
  };

  const buildAndAdvance = async () => {
    await buildReferences.mutateAsync();
    onAdvance(3);
  };

  return (
    <div className="anim-fade">
      <SectionHeader
        number={2}
        eyebrow="Etapa 02 · Curadoria"
        title="Seleção de fontes"
        lede={
          allSources.length
            ? "O sistema ranqueou as fontes pelo peso do domínio. Marque as que devem entrar na monografia. Cada fonte selecionada recebe um número de citação ABNT."
            : "Nenhuma fonte ainda. Volte para a Etapa 1 e clique em Iniciar busca."
        }
        action={
          <Button
            variant="ink"
            icon={buildReferences.isPending ? Icon.Loader : Icon.FileText}
            iconRight={buildReferences.isPending ? undefined : Icon.ArrowRight}
            disabled={selected.length === 0 || buildReferences.isPending}
            onClick={buildAndAdvance}
          >
            Construir referências e avançar
          </Button>
        }
      />

      {buildReferences.isError && (
        <div className="mb-8">
          <Callout tone="strike" icon={Icon.AlertCircle} title="Referências não geradas">
            Não foi possível construir as referências ABNT. Verifique as fontes selecionadas
            e tente novamente.
          </Callout>
        </div>
      )}

      <div className="grid grid-cols-4 gap-0 border-y-2 border-[var(--ink)] mb-10">
        <div className="px-6 py-5 border-r border-[var(--rule)]">
          <Stat value={allSources.length} label="Total" size="md" />
        </div>
        <div className="px-6 py-5 border-r border-[var(--rule)]">
          <Stat value={selected.length} label="Selecionadas" size="md" />
        </div>
        <div className="px-6 py-5 border-r border-[var(--rule)]">
          <Stat
            value={allSources.filter((s) => s.domain_weight >= 9).length}
            label="Tier 1"
            sub="peso ≥ 9"
            size="md"
          />
        </div>
        <div className="px-6 py-5">
          <Stat
            value={allSources.filter((s) => s.file_type === "pdf").length}
            label="PDFs"
            sub={`+ ${allSources.filter((s) => s.file_type === "html").length} HTML`}
            size="md"
          />
        </div>
      </div>

      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <Tabs<Filter>
          value={filter}
          onChange={setFilter}
          tabs={[
            { value: "all", label: "Todas", count: allSources.length },
            { value: "selected", label: "Selecionadas", count: selected.length },
            { value: "available", label: "Disponíveis", count: available.length },
          ]}
        />
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Button
            variant="line"
            size="sm"
            disabled={selectAll.isPending || allSources.length === 0}
            onClick={() => selectAll.mutate(true)}
          >
            Selecionar todas
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={selectAll.isPending || selected.length === 0}
            onClick={() => selectAll.mutate(false)}
          >
            Limpar seleção
          </Button>
          <Button
            variant="ink"
            size="sm"
            disabled={validateRunning || selected.length === 0}
            icon={validateRunning ? Icon.Loader : Icon.RefreshCw}
            onClick={startValidation}
          >
            Validar acesso
          </Button>
        </div>
      </div>

      <div className="flex justify-between gap-4 mb-6">
        <span className="caption">
          Ordenadas por <span className="text-[var(--ink)]">rank score</span> ↓
        </span>
        {validateJob.data?.status === "success" && (
          <span className="caption text-[var(--ok)]">
            Validação concluída ·{" "}
            {allSources.filter((s) => s.accessibility_status === "unavailable").length}{" "}
            indisponível(is)
          </span>
        )}
      </div>

      {sources.isPending ? (
        <div className="py-20 flex items-center justify-center gap-3 caption">
          <Spinner /> Carregando fontes...
        </div>
      ) : sources.isError ? (
        <Callout tone="strike" icon={Icon.AlertCircle} title="Fontes indisponíveis">
          Não foi possível carregar as fontes. Tente atualizar a página.
        </Callout>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Icon.Search}
          title="Nenhuma fonte para mostrar"
          description="A busca ainda não foi iniciada ou o filtro atual não encontrou fontes."
        />
      ) : (
        <ol className="anim-stagger">
          {filtered.map((src, i) => (
            <SourceEntry
              key={src.id}
              src={src}
              idx={i + 1}
              disabled={toggle.isPending}
              onToggle={() => toggle.mutate({ id: src.id, selected: !src.selected })}
            />
          ))}
        </ol>
      )}

      {!sources.isPending && !sources.isError && allSources.length > 0 && (
        <div className="mt-10 pt-6 border-t border-[var(--rule)] flex justify-end">
          <Button
            variant="ink"
            size="lg"
            icon={buildReferences.isPending ? Icon.Loader : Icon.FileText}
            iconRight={buildReferences.isPending ? undefined : Icon.ArrowRight}
            disabled={selected.length === 0 || buildReferences.isPending}
            onClick={buildAndAdvance}
          >
            Construir referencias e avancar
          </Button>
        </div>
      )}
    </div>
  );
}

interface SourceEntryProps {
  src: Source;
  idx: number;
  disabled: boolean;
  onToggle: () => void;
}

function SourceEntry({ src, idx, disabled, onToggle }: SourceEntryProps) {
  const badge = fileBadge(src);
  const title = src.title || src.url;
  return (
    <li className="grid grid-cols-12 gap-6 py-6 border-b border-[var(--rule-2)]">
      <div className="col-span-1 flex flex-col items-start gap-2">
        <span className="step-num text-[24px] tabular text-[var(--ink-4)]">
          {String(idx).padStart(2, "0")}
        </span>
        <button
          type="button"
          onClick={onToggle}
          disabled={disabled}
          aria-pressed={src.selected}
          aria-label={`Selecionar fonte: ${title}`}
          className={`w-5 h-5 border flex items-center justify-center transition-colors ${src.selected ? "bg-[var(--ink)] border-[var(--ink)]" : "bg-[var(--paper)] border-[var(--ink-3)] hover:border-[var(--ink)]"}`}
        >
          {src.selected && (
            <Icon.Check size={13} strokeWidth={3} className="text-[var(--paper)]" />
          )}
        </button>
      </div>

      <div className="col-span-12 md:col-span-7 -mt-1">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Tag tone="ink">{src.domain}</Tag>
          <Tag tone={badge.tone}>{badge.label}</Tag>
          {src.publish_year && (
            <span className="mono text-[11px] text-[var(--ink-3)] tabular">
              {src.publish_year}
            </span>
          )}
          {src.selected && src.citation_number != null && (
            <Tag tone="solid">Citação ({src.citation_number})</Tag>
          )}
        </div>
        <h3 className="display-5 leading-tight mb-2 text-[var(--ink)]">{title}</h3>
        {src.snippet && (
          <p className="body text-[13.5px] leading-relaxed line-clamp-2 max-w-2xl mb-2">
            {src.snippet}
          </p>
        )}
        <div className="flex items-baseline gap-3 mono text-[11px] text-[var(--ink-3)]">
          <span>{src.document_type ?? "generico"}</span>
          <span className="text-[var(--ink-4)]">·</span>
          <a
            href={src.url}
            target="_blank"
            rel="noreferrer"
            className="hover:text-[var(--ink)] flex items-center gap-1"
          >
            abrir <Icon.ExternalLink size={10} />
          </a>
        </div>
      </div>

      <div className="col-span-12 md:col-span-4 flex flex-col items-end justify-start gap-3 -mt-1">
        <div className="text-right">
          <div className="num-md text-[var(--ink)]">
            {src.rank_score != null ? src.rank_score.toFixed(0) : "—"}
          </div>
          <div className="caption">rank score</div>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-right">
          <span className="caption">Peso</span>
          <span className="mono text-[12px] text-[var(--ink)] tabular">
            w{src.domain_weight}
          </span>
          <span className="caption">Descritor</span>
          <span className="mono text-[12px] text-[var(--ink)] tabular">
            {src.descriptor_used ?? "—"}
          </span>
        </div>
      </div>
    </li>
  );
}
